import React, { useEffect, useMemo, useState } from "react";
import { PAGINATION_PAGE_SIZE_OPTIONS } from "../../config/pagination";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Snackbar,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add,
  Check,
  Close,
  Delete,
  Edit,
  EventNote,
  People,
  Refresh,
  School,
  Search,
  Settings,
} from "@mui/icons-material";
import {
  useCreateLeaveManagementType,
  useDeleteLeaveManagementType,
  useLeaveManagementList,
  useUpdateLeaveManagementStatus,
  useUpdateLeaveManagementType,
  useAllocations,
  useCreateAllocation,
  useUpdateAllocation,
  useDeleteAllocation,
} from "../../hooks/queries/useLeaves";
import PrimaryButton from "../../components/ui/PrimaryButton";
import PermissionGate from "../../components/common/PermissionGate";

const createDefaultLeaveTypeForm = () => ({
  name: "",
  code: "",
  description: "",
  color: "#1976d2",
  applicableTo: "both",
  requiresApproval: true,
  requiresDocument: false,
  isActive: true,
  maxConsecutiveDays: "",
  minAdvanceNotice: 1,
});

const getCurrentAcademicYear = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const startYear = m >= 6 ? y : y - 1;
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
};

const createDefaultAllocationForm = () => ({
  leaveTypeId: "",
  userType: "student",
  academicYear: getCurrentAcademicYear(),
  totalDays: "",
  carryForward: false,
  maxCarryForwardDays: 0,
});

const getBoolean = (value, fallback = false) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    return ["true", "1", "yes", "active", "enabled"].includes(
      value.toLowerCase()
    );
  }
  return fallback;
};

const CARRY_FORWARD_EXCEEDS_ALLOCATION_MSG =
  "Carry Forward Days cannot exceed the total leave allocation for this leave type.";

const isAllocationCarryForwardInvalid = (allocation) => {
  if (!allocation) return false;
  const carryForward = getBoolean(
    allocation.carryForward ?? allocation.allowCarryForward,
    false
  );
  if (!carryForward) return false;
  const total = Number(allocation.totalDays ?? allocation.days);
  const maxCf = Number(
    allocation.maxCarryForwardDays ?? allocation.carryForwardDays ?? 0
  );
  if (!Number.isFinite(total) || total < 1) return false;
  return Number.isFinite(maxCf) && maxCf > total;
};

const getApplicableToLabel = (value) => {
  switch ((value || "").toLowerCase()) {
    case "student":
      return "Students Only";
    case "staff":
      return "Staff Only";
    case "both":
      return "Students & Staff";
    default:
      return value || "-";
  }
};

const LeaveManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pagination, setPagination] = useState({ page: 0, limit: 10 });
  const [sortModel] = useState({ sort: "", order: "DESC" });
  const [leaveTypeDialogOpen, setLeaveTypeDialogOpen] = useState(false);
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [editingLeaveType, setEditingLeaveType] = useState(null);
  const [editingAllocation, setEditingAllocation] = useState(null);
  const [leaveTypeForm, setLeaveTypeForm] = useState(createDefaultLeaveTypeForm());
  const [allocationForm, setAllocationForm] = useState(createDefaultAllocationForm());
  const [leaveTypeTouched, setLeaveTypeTouched] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [leaveTypeToDelete, setLeaveTypeToDelete] = useState(null);
  const [updatingIds, setUpdatingIds] = useState([]);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSnackbarClose = (_event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const queryParams = useMemo(() => {
    const params = {
      page: pagination.page + 1,
      limit: pagination.limit,
      order: sortModel.order,
    };

    if (sortModel.sort) {
      params.sort = sortModel.sort;
    }

    if (debouncedSearch) {
      params.search = debouncedSearch;
    }

    return params;
  }, [pagination, sortModel, debouncedSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPagination((prev) => ({ ...prev, page: 0 }));
    }, 400);

    return () => clearTimeout(handler);
  }, [search]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useLeaveManagementList(queryParams);
  const {
    data: allocationsData,
    isLoading: allocationsLoading,
    isError: allocationsError,
    error: allocationsFetchError,
  } = useAllocations();
  const createAllocationMutation = useCreateAllocation();
  const updateAllocationMutation = useUpdateAllocation();
  const deleteAllocationMutation = useDeleteAllocation();
  const isSavingAllocation =
    createAllocationMutation.isPending || updateAllocationMutation.isPending;
  const {
    mutateAsync: createLeaveType,
    isPending: isCreatingLeaveType,
  } = useCreateLeaveManagementType();
  const { mutateAsync: updateLeaveType, isPending: isUpdatingLeaveType } =
    useUpdateLeaveManagementType();
  const { mutateAsync: deleteLeaveType, isPending: isDeletingLeaveType } =
    useDeleteLeaveManagementType();
  const { mutateAsync: updateLeaveStatus, isPending: isUpdatingStatus } =
    useUpdateLeaveManagementStatus();

  const isSavingLeaveType = isCreatingLeaveType || isUpdatingLeaveType;

  const handleLeaveTypeStatusToggle = async (leaveType, nextStatus) => {
    const id = resolveLeaveTypeId(leaveType);
    if (!id) return;

    // mark this id as updating
    setUpdatingIds((prev) => Array.from(new Set([...prev, id])));

    try {
      await updateLeaveStatus({ id, status: nextStatus });
      // refresh list after update
      refetch();
      showSnackbar(
        nextStatus
          ? "Leave type activated successfully."
          : "Leave type deactivated successfully."
      );
    } catch (error) {
      console.error("Failed to update leave type status", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to update leave type status.";
      showSnackbar(errorMessage, "error");
    } finally {
      // remove id from updating list
      setUpdatingIds((prev) => prev.filter((x) => x !== id));
    }
  };

  const openDeleteConfirmation = (leaveType) => {
    setLeaveTypeToDelete(leaveType);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirmation = () => {
    setDeleteConfirmOpen(false);
    setLeaveTypeToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!leaveTypeToDelete) return;
    await handleDeleteLeaveType(leaveTypeToDelete);
    closeDeleteConfirmation();
  };

  const { leaveTypeErrors, isLeaveTypeFormValid } = useMemo(() => {
    const errors = {};

    const name = leaveTypeForm.name.trim();
    if (name.length < 3 || name.length > 50) {
      errors.name = "Name must be between 3 and 50 characters.";
    }

    const code = leaveTypeForm.code.trim();
    if (code.length < 2 || code.length > 10) {
      errors.code = "Code must be between 2 and 10 characters.";
    }

    const description = leaveTypeForm.description.trim();
    if (description && (description.length < 3 || description.length > 100)) {
      errors.description = "Description must be 3-100 characters if provided.";
    }

    const maxConsecutiveRaw = leaveTypeForm.maxConsecutiveDays;
    const maxConsecutiveEmpty = maxConsecutiveRaw === "" || maxConsecutiveRaw === undefined || maxConsecutiveRaw === null;
    if (maxConsecutiveEmpty) {
      errors.maxConsecutiveDays = "Max consecutive days is required.";
    } else {
      const maxDaysValue = Number(maxConsecutiveRaw);
      if (Number.isNaN(maxDaysValue) || maxDaysValue < 0 || maxDaysValue > 365) {
        errors.maxConsecutiveDays = "Max days must be between 0 and 365.";
      }
    }

    const minNoticeRaw = leaveTypeForm.minAdvanceNotice;
    const minNoticeEmpty = minNoticeRaw === "" || minNoticeRaw === undefined || minNoticeRaw === null;
    if (minNoticeEmpty) {
      errors.minAdvanceNotice = "Minimum advance notice is required.";
    } else {
      const noticeValue = Number(minNoticeRaw);
      if (Number.isNaN(noticeValue) || noticeValue < 0 || noticeValue > 180) {
        errors.minAdvanceNotice = "Advance notice must be between 0 and 180.";
      }
    }

    // Only show errors for touched fields
    const filteredErrors = {};
    Object.keys(errors).forEach(field => {
      if (leaveTypeTouched[field]) {
        filteredErrors[field] = errors[field];
      }
    });

    return {
      leaveTypeErrors: filteredErrors,
      isLeaveTypeFormValid: Object.keys(errors).length === 0,
    };
  }, [leaveTypeForm, leaveTypeTouched]);

  const leaveTypesForValidation =
    data?.leaveTypes || data?.data?.leaveTypes || data?.data?.data?.leaveTypes || [];
  const selectedLeaveTypeMaxDays = useMemo(() => {
    if (!allocationForm.leaveTypeId) return null;
    const selected = leaveTypesForValidation.find(
      (lt) => String(lt?.id ?? lt?._id ?? lt?.leaveTypeId ?? "") === String(allocationForm.leaveTypeId)
    );
    const max = selected?.maxConsecutiveDays ?? selected?.maxDays ?? selected?.maxConsecutive;
    if (max == null || max === "") return null;
    const num = Number(max);
    return Number.isNaN(num) ? null : num;
  }, [allocationForm.leaveTypeId, leaveTypesForValidation]);

  const allocationTotalDaysCap = useMemo(() => {
    const n = Number(allocationForm.totalDays);
    return Number.isFinite(n) && n >= 1 ? n : null;
  }, [allocationForm.totalDays]);

  const { allocationErrors, isAllocationFormValid } = useMemo(() => {
    const errors = {};

    if (!allocationForm.leaveTypeId) {
      errors.leaveTypeId = "Leave type is required.";
    }

    const totalDaysValue = Number(allocationForm.totalDays);
    const totalDaysEmpty = allocationForm.totalDays === "" || allocationForm.totalDays === undefined || allocationForm.totalDays === null;
    const totalDaysInvalid = totalDaysEmpty || Number.isNaN(totalDaysValue);

    if (totalDaysInvalid || totalDaysValue < 1) {
      const maxCap = selectedLeaveTypeMaxDays ?? 365;
      errors.totalDays = `Total days must be between 1 and ${maxCap}.`;
    } else if (selectedLeaveTypeMaxDays != null && totalDaysValue > selectedLeaveTypeMaxDays) {
      errors.totalDays = `Total days cannot exceed this leave type's max (${selectedLeaveTypeMaxDays} day(s)).`;
    } else if (selectedLeaveTypeMaxDays == null && totalDaysValue > 365) {
      errors.totalDays = "Total days must be between 1 and 365.";
    }

    if (allocationForm.carryForward) {
      const raw = allocationForm.maxCarryForwardDays;
      const isEmpty = raw === "" || raw === undefined || raw === null;
      const maxCarryValue = Number(raw);
      const totalForCap =
        !totalDaysInvalid && Number.isFinite(totalDaysValue) ? totalDaysValue : null;

      if (isEmpty) {
        errors.maxCarryForwardDays = "Max carry forward days is required.";
      } else if (Number.isNaN(maxCarryValue) || maxCarryValue < 0) {
        errors.maxCarryForwardDays = "Max carry forward days cannot be negative.";
      } else if (totalForCap != null && maxCarryValue > totalForCap) {
        errors.maxCarryForwardDays = CARRY_FORWARD_EXCEEDS_ALLOCATION_MSG;
      }
    }

    return {
      allocationErrors: errors,
      isAllocationFormValid: Object.keys(errors).length === 0,
    };
  }, [allocationForm, leaveTypesForValidation, selectedLeaveTypeMaxDays]);

  const leaveTypes =
    data?.leaveTypes || data?.data?.leaveTypes || data?.data?.data?.leaveTypes || [];
  const activeLeaveTypes = Array.isArray(leaveTypes)
    ? leaveTypes.filter((t) => (t?.status ?? t?.isActive) !== false && !t?.isDeleted)
    : [];
  const allocations =
    data?.allocations ||
    data?.data?.allocations ||
    data?.data?.data?.allocations ||
    [];

  // if allocations endpoint is separate, merge allocations from allocationsData
  const fetchedAllocations = allocationsData?.data || allocationsData || [];
  const combinedAllocations = (fetchedAllocations.length > 0 ? fetchedAllocations : allocations) || [];
  const invalidCarryForwardAllocations = useMemo(
    () => combinedAllocations.filter(isAllocationCarryForwardInvalid),
    [combinedAllocations]
  );
  const stats = data?.stats || data?.data?.stats || data?.data?.data?.stats || {};
  const counts = data?.counts || data?.data?.counts || data?.data?.data?.counts || {};
  const paginationMeta =
    data?.pagination || data?.data?.pagination || data?.data?.data?.pagination || {};

  const totalLeaveTypes =
    counts.totalLeaves ?? paginationMeta.total ?? stats.totalLeaveTypes ?? leaveTypes.length;

  const handleTabChange = (_event, newValue) => {
    setActiveTab(newValue);
  };

  const handlePageChange = (_event, newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = parseInt(event.target.value, 10) || 10;
    setPagination({ page: 0, limit: newLimit });
  };

  const resolveLeaveTypeId = (leaveType) =>
    leaveType.id || leaveType._id || leaveType.leaveTypeId || leaveType.uuid;

  const resolveAllocationId = (allocation, index) =>
    allocation.id ||
    allocation._id ||
    allocation.leaveAllocationId ||
    `${allocation.leaveTypeId}-${allocation.userType}-${allocation.academicYear}-${index}`;

  const renderStatusChip = (isActive) => {
    const active = getBoolean(isActive);
    return (
      <Chip
        label={active ? "Active" : "Inactive"}
        size="small"
        color={active ? "success" : "default"}
        variant={active ? "filled" : "outlined"}
      />
    );
  };

  const handleOpenLeaveTypeDialog = (leaveType = null) => {
    if (leaveType) {
      const applicableSource = (
        leaveType.applicableTo ||
        leaveType.applicableFor ||
        leaveType.applicable ||
        "both"
      )
        .toString()
        .toLowerCase();
      const applicableTo = ["student", "staff", "both"].includes(applicableSource)
        ? applicableSource
        : "both";

      setEditingLeaveType(leaveType);
      setLeaveTypeForm({
        name: leaveType.name || leaveType.leaveTypeName || "",
        code: leaveType.code || leaveType.leaveTypeCode || "",
        description:
          leaveType.description || leaveType.leaveTypeDescription || "",
        color: leaveType.color || leaveType.colour || "#1976d2",
        applicableTo,
        requiresApproval: getBoolean(
          leaveType.requiresApproval ??
              leaveType.requiredApproval ??
            leaveType.approvalRequired ??
            leaveType.needsApproval,
          false
        ),
        requiresDocument: getBoolean(
          leaveType.requiredDocument ??
          leaveType.requiresDocument ??
            leaveType.documentRequired ??
            leaveType.isDocumentRequired,
          false
        ),
        isActive: getBoolean(leaveType.isActive ?? leaveType.status, true),
        maxConsecutiveDays:
          leaveType.maxConsecutiveDays ??
          leaveType.maxDays ??
          leaveType.maxConsecutive ??
          "",
        minAdvanceNotice:
          leaveType.advanceNoticeDay ??
          leaveType.minAdvanceNotice ??
          leaveType.minimumNotice ??
          leaveType.advanceNotice ??
          1,
      });
    } else {
      setEditingLeaveType(null);
      setLeaveTypeForm(createDefaultLeaveTypeForm());
    }

    setLeaveTypeTouched({});
    setLeaveTypeDialogOpen(true);
  };

  const handleCloseLeaveTypeDialog = () => {
    setLeaveTypeDialogOpen(false);
    setEditingLeaveType(null);
    setLeaveTypeForm(createDefaultLeaveTypeForm());
    setLeaveTypeTouched({});
  };

  // Block number keys in Leave Type Name (letters and spaces only)
  const blockNumberKeyDown = (e) => {
    if (/^[0-9]$/.test(e.key)) e.preventDefault();
  };
  const handleLeaveTypeNameChange = (e) => {
    const value = e.target.value.replace(/\d/g, "").slice(0, 50);
    setLeaveTypeForm((prev) => ({ ...prev, name: value }));
  };

  const handleLeaveTypeFieldBlur = (field) => {
    setLeaveTypeTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleLeaveTypeSubmit = async () => {
    const resolvedId =
      editingLeaveType?.id ||
      editingLeaveType?._id ||
      editingLeaveType?.leaveTypeId ||
      editingLeaveType?.uuid;
    const payload = {
      leaveTypeName: leaveTypeForm.name.trim(),
      code: leaveTypeForm.code.trim(),
      description: leaveTypeForm.description.trim() || undefined,
      applicableTo: leaveTypeForm.applicableTo,
      maxConsecutiveDays: leaveTypeForm.maxConsecutiveDays
        ? Number(leaveTypeForm.maxConsecutiveDays)
        : undefined,
      advanceNoticeDay: leaveTypeForm.minAdvanceNotice
        ? Number(leaveTypeForm.minAdvanceNotice)
        : undefined,
      requiredApproval: !!leaveTypeForm.requiresApproval,
      requiredDocument: !!leaveTypeForm.requiresDocument,
      status: !!leaveTypeForm.isActive,
      color: leaveTypeForm.color,
    };

    try {
      if (resolvedId) {
        await updateLeaveType({ id: resolvedId, payload });
        showSnackbar("Leave type updated successfully.");
      } else {
        await createLeaveType(payload);
        showSnackbar("Leave type created successfully.");
      }
      setLeaveTypeDialogOpen(false);
      setEditingLeaveType(null);
      setLeaveTypeForm(createDefaultLeaveTypeForm());
      refetch();
    } catch (error) {
      console.error("Failed to save leave type", error);
      const data = error?.response?.data;
      const errorMessage =
        data?.error ||
        data?.message ||
        error?.message ||
        "Failed to save leave type.";
      showSnackbar(errorMessage, "error");
    }
  };

  const handleDeleteLeaveType = async (leaveType) => {
    const id = resolveLeaveTypeId(leaveType);
    if (!id) return;

    try {
      await deleteLeaveType(id);
      refetch();
      showSnackbar("Leave type deleted successfully.");
    } catch (error) {
      console.error("Failed to delete leave type", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete leave type.";
      showSnackbar(errorMessage, "error");
    }
  };

  const handleOpenAllocationDialog = (allocation = null) => {
    if (allocation) {
      const resolvedLeaveTypeId =
        allocation.leaveTypeId ||
        allocation.leaveType?._id ||
        allocation.leaveType?.id ||
        allocation.leaveType?.leaveTypeId;

      setEditingAllocation(allocation);
      setAllocationForm({
        leaveTypeId: resolvedLeaveTypeId
          ? String(resolvedLeaveTypeId)
          : "",
        userType: (allocation.userType || "student").toLowerCase(),
        academicYear: allocation.academicYear || allocation.year || "2024-25",
        totalDays: allocation.totalDays ?? allocation.days ?? "",
        carryForward: getBoolean(
          allocation.carryForward ?? allocation.allowCarryForward,
          false
        ),
        maxCarryForwardDays: getBoolean(allocation.carryForward ?? allocation.allowCarryForward, false)
          ? (allocation.maxCarryForwardDays ?? allocation.carryForwardDays ?? 0)
          : 0,
      });
    } else {
      const defaultForm = createDefaultAllocationForm();
      const firstLeaveTypeId =
        activeLeaveTypes.length > 0
          ? String(resolveLeaveTypeId(activeLeaveTypes[0]))
          : "";

      setEditingAllocation(null);
      setAllocationForm({
        ...defaultForm,
        leaveTypeId: firstLeaveTypeId,
      });
    }

    setAllocationDialogOpen(true);
  };

  const handleCloseAllocationDialog = () => {
    setAllocationDialogOpen(false);
    setEditingAllocation(null);
    setAllocationForm(createDefaultAllocationForm());
  };

  const handleAllocationSubmit = () => {
    if (!isAllocationFormValid) return;

    const carryForward = !!allocationForm.carryForward;
    const payload = {
      leaveTypeId: allocationForm.leaveTypeId,
      userType: allocationForm.userType,
      academicYear: allocationForm.academicYear,
      totalDays: Number(allocationForm.totalDays),
      carryForward,
      maxCarryForwardDays: carryForward ? (Number(allocationForm.maxCarryForwardDays) || 0) : 0,
    };

    if (editingAllocation && editingAllocation.id) {
      updateAllocationMutation.mutate({ id: editingAllocation.id, payload }, {
        onSuccess: () => {
          showSnackbar('Allocation updated');
          refetch();
          setAllocationDialogOpen(false);
          setEditingAllocation(null);
          setAllocationForm(createDefaultAllocationForm());
        },
        onError: (err) => {
          console.error('Update allocation failed', err);
          const errorMessage =
            err?.response?.data?.message ||
            err?.message ||
            'Update failed';
          showSnackbar(errorMessage, 'error');
        }
      });
    } else {
      createAllocationMutation.mutate(payload, {
        onSuccess: () => {
          showSnackbar('Allocation created');
          refetch();
          setAllocationDialogOpen(false);
          setEditingAllocation(null);
          setAllocationForm(createDefaultAllocationForm());
        },
        onError: (err) => {
          console.error('Create allocation failed', err);
          const errorMessage =
            err?.response?.data?.message ||
            err?.message ||
            'Create failed';
          showSnackbar(errorMessage, 'error');
        }
      });
    }
  };

  const academicYearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const options = [];
    for (let offset = -1; offset <= 2; offset += 1) {
      const startYear = currentYear + offset;
      const endYearShort = String((startYear + 1) % 100).padStart(2, "0");
      options.push(`${startYear}-${endYearShort}`);
    }
    return options;
  }, []);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Leave Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure leave types and allocations for students and staff
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.5}>
          <TextField
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search leave types"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: isFetching ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : undefined,
            }}
            sx={{ minWidth: { xs: 220, sm: 260 } }}
          />
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={() => refetch()} disabled={isFetching}>
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          {
            label: "Total Leave Types",
            value: counts.totalLeaves ?? stats.totalLeaveTypes ?? leaveTypes.length,
            color: "primary.main",
            icon: <EventNote fontSize="small" />,
          },
          {
            label: "Active Types",
            value:
              counts.totalActive ??
              stats.activeLeaveTypes ??
              leaveTypes.filter((lt) => getBoolean(lt.isActive, true)).length,
            color: "success.main",
            icon: <Check fontSize="small" />,
          },
          {
            label: "Student Allocations",
            value:
              counts.studentAllocation ??
              stats.studentAllocations ??
              allocations.filter((allocation) => (allocation.userType || "").toLowerCase() === "student").length,
            color: "info.main",
            icon: <School fontSize="small" />,
          },
          {
            label: "Staff Allocations",
            value:
              counts.staffAllocation ??
              stats.staffAllocations ??
              allocations.filter((allocation) => (allocation.userType || "").toLowerCase() === "staff").length,
            color: "warning.main",
            icon: <People fontSize="small" />,
          },
        ].map((stat, index) => (
          <Grid item xs={6} sm={3} key={index}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {stat.label}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} sx={{ color: stat.color }}>
                      {stat.value ?? "-"}
                    </Typography>
                  </Box>
                  <Avatar
                    sx={{
                      bgcolor: `${stat.color}20`,
                      color: stat.color,
                      width: 48,
                      height: 48,
                    }}
                  >
                    {stat.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Card>
        <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab icon={<EventNote />} iconPosition="start" label="Leave Types" />
          <Tab icon={<Settings />} iconPosition="start" label="Allocations" />
        </Tabs>

        <CardContent>
          {activeTab === 0 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "stretch", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Leave Types
                </Typography>
                <PermissionGate permission="leave.create">
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => handleOpenLeaveTypeDialog()}
                  >
                    Add Leave Type
                  </Button>
                </PermissionGate>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Tip:</strong> Use search and pagination controls to explore leave types.
              </Alert>

              {isError ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error?.response?.data?.message ||
                    error?.message ||
                    "Unable to load leave types. Please check your permissions or try again."}
                </Alert>
              ) : null}

              {isLoading ? (
                <Box display="flex" justifyContent="center" py={6}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ position: 'relative' }}>
                  {isFetching && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        bgcolor: 'rgba(255,255,255,0.6)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 10,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  )}
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: "grey.50" }}>
                        <TableCell>Leave Type</TableCell>
                        <TableCell sx={{ minWidth: 90 }}>Code</TableCell>
                        <TableCell sx={{ minWidth: 160 }}>Applicable To</TableCell>
                        <TableCell align="center">Max Days</TableCell>
                        <TableCell align="center">Advance Notice</TableCell>
                        <TableCell align="center">Document Required</TableCell>
                        <TableCell align="center">Status</TableCell>
                        <TableCell align="center" sx={{ minWidth: 120 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leaveTypes.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              No leave types found.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        leaveTypes.map((leaveType) => {
                          const id = resolveLeaveTypeId(leaveType);
                          const name = leaveType.name || leaveType.leaveTypeName || "-";
                          const code = leaveType.code || leaveType.leaveTypeCode || "-";
                          const description = leaveType.description || leaveType.leaveTypeDescription || "";
                          const color = leaveType.color || leaveType.colour || "#1976d2";
                          const applicableTo = leaveType.applicableTo || leaveType.applicableFor || leaveType.applicable;
                          const maxDays =
                            leaveType.maxConsecutiveDays ??
                            leaveType.maxDays ??
                            leaveType.maxConsecutive ??
                            "-";
                          const advanceNotice =
                            leaveType.advanceNoticeDay ??
                            leaveType.minAdvanceNotice ??
                            leaveType.minimumNotice ??
                            leaveType.advanceNotice ??
                            0;
                          const requiresDocument =
                            getBoolean(
                              leaveType.requiredDocument ??
                              leaveType.requiresDocument ??
                                leaveType.documentRequired ??
                                leaveType.isDocumentRequired,
                              false
                            );

                          return (
                            <TableRow key={id} hover>
                              <TableCell>
                                <Box display="flex" alignItems="center" gap={1.5}>
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: "50%",
                                      bgcolor: color,
                                    }}
                                  />
                                  <Box>
                                    <Typography variant="body2" fontWeight={600}>
                                      {name}
                                    </Typography>
                                    {description ? (
                                      <Typography variant="caption" color="text.secondary">
                                        {description}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell>
                                <Chip label={code} size="small" sx={{ bgcolor: `${color}20` }} />
                              </TableCell>
                              <TableCell>
                                <Chip
                                  icon={
                                    (applicableTo || "").toLowerCase() === "student" ? (
                                      <School fontSize="small" />
                                    ) : (applicableTo || "").toLowerCase() === "staff" ? (
                                      <People fontSize="small" />
                                    ) : (
                                      <People fontSize="small" />
                                    )
                                  }
                                  label={getApplicableToLabel(applicableTo)}
                                  size="small"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="center">{maxDays}</TableCell>
                              <TableCell align="center">{advanceNotice} day(s)</TableCell>
                              <TableCell align="center">
                                {requiresDocument ? <Check color="success" /> : <Close color="error" />}
                              </TableCell>
                              <TableCell align="center">
                                {updatingIds.includes(id) ? (
                                  <CircularProgress size={20} />
                                ) : (
                                  <Switch
                                    size="small"
                                    checked={getBoolean(leaveType.isActive ?? leaveType.status)}
                                    onChange={(event) =>
                                      handleLeaveTypeStatusToggle(leaveType, event.target.checked)
                                    }
                                    disabled={false}
                                    color="success"
                                  />
                                )}
                              </TableCell>
                              <TableCell align="center">
                                <PermissionGate permission="leave.update">
                                  <Tooltip title="Edit">
                                    <IconButton size="small" onClick={() => handleOpenLeaveTypeDialog(leaveType)}>
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </PermissionGate>
                                <PermissionGate permission="leave.delete">
                                  <Tooltip title="Delete">
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => openDeleteConfirmation(leaveType)}
                                        disabled={isDeletingLeaveType}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </PermissionGate>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <TablePagination
                component="div"
                count={totalLeaveTypes}
                page={pagination.page}
                onPageChange={handlePageChange}
                rowsPerPage={pagination.limit}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={PAGINATION_PAGE_SIZE_OPTIONS}
              />
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "stretch", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Leave Allocations
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={() => handleOpenAllocationDialog()}
                  disabled={leaveTypes.length === 0}
                >
                  Add Allocation
                </Button>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Note:</strong> Allocations define annual leave availability for each user group.
                Carry forward cannot exceed total days for the same academic year.
              </Alert>
              {invalidCarryForwardAllocations.length > 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {invalidCarryForwardAllocations.length} allocation(s) have carry forward limits
                  greater than total days. Edit each record to correct before saving other changes.
                </Alert>
              )}

              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ mt: 2, mb: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <School color="primary" fontSize="small" /> Student Leave Allocations
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3, position: 'relative' }}>
                {allocationsLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(255,255,255,0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <Table size="small">
<TableHead>
                  <TableRow sx={{ bgcolor: "primary.50" }}>
                      <TableCell>Leave Type</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Academic Year</TableCell>
                      {/* <TableCell align="center">Max Days</TableCell> */}
                      <TableCell align="center">Total Days</TableCell>
                      <TableCell align="center">Carry Forward</TableCell>
                        <TableCell align="center">Max Carry Days</TableCell>
                        <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {combinedAllocations.filter((allocation) => (allocation.userType || "").toLowerCase() === "student").map((allocation, index) => {
                      const id = resolveAllocationId(allocation, index);
                      const carryForward = getBoolean(allocation.carryForward || allocation.allowCarryForward);
                      // const allocationLeaveTypeId = allocation.leaveTypeId ?? allocation.leaveType?.id ?? allocation.leaveType?._id;
                      // const resolvedLeaveType = allocation.leaveType || (allocationLeaveTypeId && leaveTypes.find((lt) => String(resolveLeaveTypeId(lt)) === String(allocationLeaveTypeId)));
                      // const maxDays = resolvedLeaveType?.maxConsecutiveDays ?? resolvedLeaveType?.maxDays ?? allocation.maxDays ?? "-";
                      const carryInvalid = isAllocationCarryForwardInvalid(allocation);
                      return (
                        <TableRow key={id} hover sx={carryInvalid ? { bgcolor: "warning.50" } : undefined}>
                          <TableCell>{allocation.leaveType?.leaveTypeName || allocation.leaveTypeName || allocation.leaveType?.name || "-"}</TableCell>
                          <TableCell>
                            <Chip
                              label={allocation.leaveType?.code || allocation.leaveTypeCode || allocation.leaveType?.leaveTypeCode || "-"}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{allocation.academicYear || allocation.year || "-"}</TableCell>
                          {/* <TableCell align="center">{maxDays}</TableCell> */}
                          <TableCell align="center">{allocation.totalDays ?? allocation.days ?? "-"}</TableCell>
                          <TableCell align="center">
                            {carryForward ? <Check color="success" /> : <Close color="error" />}
                          </TableCell>
                          <TableCell align="center">
                            {carryForward ? (
                              carryInvalid ? (
                                <Tooltip title={CARRY_FORWARD_EXCEEDS_ALLOCATION_MSG}>
                                  <Chip
                                    size="small"
                                    color="warning"
                                    label={allocation.maxCarryForwardDays ?? allocation.carryForwardDays ?? 0}
                                  />
                                </Tooltip>
                              ) : (
                                allocation.maxCarryForwardDays ?? allocation.carryForwardDays ?? 0
                              )
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <PermissionGate permission="leave.update">
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleOpenAllocationDialog(allocation)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </PermissionGate>
                            <PermissionGate permission="leave.delete">
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => {
                                  if (!allocation.id) return showSnackbar('Cannot delete allocation without id', 'error');
                                  deleteAllocationMutation.mutate(allocation.id, {
                                    onSuccess: () => { showSnackbar('Allocation deleted'); refetch(); },
                                    onError: (err) => { console.error('Delete allocation failed', err); showSnackbar('Delete failed', 'error'); }
                                  });
                                }}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </PermissionGate>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {combinedAllocations.filter((allocation) => (allocation.userType || "").toLowerCase() === "student").length === 0 && (
                      <TableRow>
<TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                            No student allocations available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ mt: 2, mb: 1, display: "flex", alignItems: "center", gap: 1 }}
              >
                <People color="secondary" fontSize="small" /> Staff Leave Allocations
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ position: 'relative' }}>
                {allocationsLoading && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: 'rgba(255,255,255,0.6)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 10,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )}
                <Table size="small">
<TableHead>
                  <TableRow sx={{ bgcolor: "secondary.50" }}>
                      <TableCell>Leave Type</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Academic Year</TableCell>
                      {/* <TableCell align="center">Max Days</TableCell> */}
                      <TableCell align="center">Total Days</TableCell>
                      <TableCell align="center">Carry Forward</TableCell>
                        <TableCell align="center">Max Carry Days</TableCell>
                        <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {combinedAllocations.filter((allocation) => (allocation.userType || "").toLowerCase() === "staff").map((allocation, index) => {
                      const id = resolveAllocationId(allocation, index);
                      const carryForward = getBoolean(allocation.carryForward || allocation.allowCarryForward);
                      // const allocationLeaveTypeId = allocation.leaveTypeId ?? allocation.leaveType?.id ?? allocation.leaveType?._id;
                      // const resolvedLeaveType = allocation.leaveType || (allocationLeaveTypeId && leaveTypes.find((lt) => String(resolveLeaveTypeId(lt)) === String(allocationLeaveTypeId)));
                      // const maxDays = resolvedLeaveType?.maxConsecutiveDays ?? resolvedLeaveType?.maxDays ?? allocation.maxDays ?? "-";
                      const carryInvalid = isAllocationCarryForwardInvalid(allocation);
                      return (
                        <TableRow key={id} hover sx={carryInvalid ? { bgcolor: "warning.50" } : undefined}>
                          <TableCell>{allocation.leaveType?.leaveTypeName || allocation.leaveTypeName || allocation.leaveType?.name || "-"}</TableCell>
                          <TableCell>
                            <Chip
                              label={allocation.leaveType?.code || allocation.leaveTypeCode || allocation.leaveType?.leaveTypeCode || "-"}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{allocation.academicYear || allocation.year || "-"}</TableCell>
                          {/* <TableCell align="center">{maxDays}</TableCell> */}
                          <TableCell align="center">{allocation.totalDays ?? allocation.days ?? "-"}</TableCell>
                          <TableCell align="center">
                            {carryForward ? <Check color="success" /> : <Close color="error" />}
                          </TableCell>
                          <TableCell align="center">
                            {carryForward ? (
                              carryInvalid ? (
                                <Tooltip title={CARRY_FORWARD_EXCEEDS_ALLOCATION_MSG}>
                                  <Chip
                                    size="small"
                                    color="warning"
                                    label={allocation.maxCarryForwardDays ?? allocation.carryForwardDays ?? 0}
                                  />
                                </Tooltip>
                              ) : (
                                allocation.maxCarryForwardDays ?? allocation.carryForwardDays ?? 0
                              )
                            ) : (
                              "-"
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <PermissionGate permission="leave.update">
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleOpenAllocationDialog(allocation)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </PermissionGate>
                            <PermissionGate permission="leave.delete">
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => {
                                  if (!allocation.id) return showSnackbar('Cannot delete allocation without id', 'error');
                                  deleteAllocationMutation.mutate(allocation.id, {
                                    onSuccess: () => { showSnackbar('Allocation deleted'); refetch(); },
                                    onError: (err) => { console.error('Delete allocation failed', err); showSnackbar('Delete failed', 'error'); }
                                  });
                                }}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </PermissionGate>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {combinedAllocations.filter((allocation) => (allocation.userType || "").toLowerCase() === "staff").length === 0 && (
                      <TableRow>
<TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                            No staff allocations available.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={leaveTypeDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleCloseLeaveTypeDialog();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingLeaveType ? "Edit Leave Type" : "Add Leave Type"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Leave Type Name"
                value={leaveTypeForm.name}
                onChange={handleLeaveTypeNameChange}
                onBlur={() => handleLeaveTypeFieldBlur('name')}
                onKeyDown={blockNumberKeyDown}
                fullWidth
                required
                placeholder="Casual Leave"
                error={Boolean(leaveTypeErrors.name)}
                helperText={leaveTypeErrors.name}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Code"
                value={leaveTypeForm.code}
                onChange={(event) =>
                  setLeaveTypeForm((prev) => ({
                    ...prev,
                    code: event.target.value.toUpperCase(),
                  }))
                }
                onBlur={() => handleLeaveTypeFieldBlur('code')}
                fullWidth
                required
                placeholder="CL"
                inputProps={{ maxLength: 10 }}
                error={Boolean(leaveTypeErrors.code)}
                helperText={leaveTypeErrors.code}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                value={leaveTypeForm.description}
                onChange={(event) =>
                  setLeaveTypeForm((prev) => ({
                    ...prev,
                    description: event.target.value,
                  }))
                }
                onBlur={() => handleLeaveTypeFieldBlur('description')}
                fullWidth
                multiline
                rows={2}
                placeholder="Describe how this leave type should be used"
                error={Boolean(leaveTypeErrors.description)}
                helperText={leaveTypeErrors.description}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Applicable To</InputLabel>
                <Select
                  label="Applicable To"
                  value={leaveTypeForm.applicableTo}
                  onChange={(event) =>
                    setLeaveTypeForm((prev) => ({
                      ...prev,
                      applicableTo: event.target.value,
                    }))
                  }
                >
                  <MenuItem value="student">Students Only</MenuItem>
                  <MenuItem value="staff">Staff Only</MenuItem>
                  <MenuItem value="both">Students & Staff</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Color"
                type="color"
                value={leaveTypeForm.color}
                onChange={(event) =>
                  setLeaveTypeForm((prev) => ({
                    ...prev,
                    color: event.target.value,
                  }))
                }
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max Consecutive Days"
                required
                type="number"
                value={leaveTypeForm.maxConsecutiveDays}
                onKeyDown={(e) => {
                  const key = e.key;
                  const isDigit = /^\d$/.test(key);
                  const isControl = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(key) || e.ctrlKey || e.metaKey;
                  if (!isDigit && !isControl) e.preventDefault();
                  const cur = String(leaveTypeForm.maxConsecutiveDays ?? "").replace(/\D/g, "");
                  if (isDigit && (cur.length >= 3 || Number(cur + key) > 365)) e.preventDefault();
                }}
                onChange={(event) => {
                  const raw = event.target.value.replace(/\D/g, "");
                  const val = raw === "" ? "" : String(Math.min(365, Math.max(0, Number(raw))));
                  setLeaveTypeForm((prev) => ({ ...prev, maxConsecutiveDays: val }));
                }}
                onBlur={() => handleLeaveTypeFieldBlur('maxConsecutiveDays')}
                fullWidth
                inputProps={{ min: 0, max: 365, inputMode: "numeric" }}
                error={Boolean(leaveTypeErrors.maxConsecutiveDays)}
                helperText={leaveTypeErrors.maxConsecutiveDays}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Minimum Advance Notice (days)"
                required
                type="number"
                value={leaveTypeForm.minAdvanceNotice}
                onKeyDown={(e) => {
                  const key = e.key;
                  const isDigit = /^\d$/.test(key);
                  const isControl = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(key) || e.ctrlKey || e.metaKey;
                  if (!isDigit && !isControl) e.preventDefault();
                  const cur = String(leaveTypeForm.minAdvanceNotice ?? "").replace(/\D/g, "");
                  if (isDigit && (cur.length >= 3 || Number(cur + key) > 180)) e.preventDefault();
                }}
                onChange={(event) => {
                  const raw = event.target.value.replace(/\D/g, "");
                  const val = raw === "" ? "" : String(Math.min(180, Math.max(0, Number(raw))));
                  setLeaveTypeForm((prev) => ({ ...prev, minAdvanceNotice: val }));
                }}
                onBlur={() => handleLeaveTypeFieldBlur('minAdvanceNotice')}
                fullWidth
                inputProps={{ min: 0, max: 180, inputMode: "numeric" }}
                error={Boolean(leaveTypeErrors.minAdvanceNotice)}
                helperText={leaveTypeErrors.minAdvanceNotice}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ mt: 1, mb: 1.5 }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={leaveTypeForm.requiresApproval}
                    onChange={(event) =>
                      setLeaveTypeForm((prev) => ({
                        ...prev,
                        requiresApproval: event.target.checked,
                      }))
                    }
                  />
                }
                label="Requires Approval"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={leaveTypeForm.requiresDocument}
                    onChange={(event) =>
                      setLeaveTypeForm((prev) => ({
                        ...prev,
                        requiresDocument: event.target.checked,
                      }))
                    }
                  />
                }
                label="Document Required"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={leaveTypeForm.isActive}
                    onChange={(event) =>
                      setLeaveTypeForm((prev) => ({
                        ...prev,
                        isActive: event.target.checked,
                      }))
                    }
                    color="success"
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLeaveTypeDialog}>Cancel</Button>
          <PrimaryButton
            onClick={handleLeaveTypeSubmit}
            disabled={!isLeaveTypeFormValid || isSavingLeaveType}
          >
            {isSavingLeaveType ? "Saving..." : editingLeaveType ? "Update" : "Create"}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={allocationDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          handleCloseAllocationDialog();
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingAllocation ? "Edit Allocation" : "Add Allocation"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel required>Leave Type</InputLabel>
                <Select
                  label="Leave Type"
                  value={allocationForm.leaveTypeId}
                  onChange={(event) =>
                    setAllocationForm((prev) => ({
                      ...prev,
                      leaveTypeId: event.target.value,
                    }))
                  }
                  disabled={activeLeaveTypes.length === 0}
                  error={Boolean(allocationErrors.leaveTypeId)}
                >
                  {activeLeaveTypes.map((leaveType) => {
                    const id = resolveLeaveTypeId(leaveType);
                    const label = `${leaveType.name || leaveType.leaveTypeName || "Untitled"} (${leaveType.code || leaveType.leaveTypeCode || "-"})`;
                    return (
                      <MenuItem value={String(id)} key={id}>
                        {label}
                      </MenuItem>
                    );
                  })}
                </Select>
                {allocationErrors.leaveTypeId ? (
                  <FormHelperText error>{allocationErrors.leaveTypeId}</FormHelperText>
                ) : null}
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>User Type</InputLabel>
                <Select
                  label="User Type"
                  value={allocationForm.userType}
                  onChange={(event) =>
                    setAllocationForm((prev) => ({
                      ...prev,
                      userType: event.target.value,
                    }))
                  }
                >
                  <MenuItem value="student">Student</MenuItem>
                  <MenuItem value="staff">Staff</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Academic Year</InputLabel>
                <Select
                  label="Academic Year"
                  value={allocationForm.academicYear}
                  onChange={(event) =>
                    setAllocationForm((prev) => ({
                      ...prev,
                      academicYear: event.target.value,
                    }))
                  }
                >
                  {academicYearOptions.map((option) => (
                    <MenuItem value={option} key={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Total Days"
                required
                type="number"
                value={allocationForm.totalDays}
                onKeyDown={(e) => {
                  const key = e.key;
                  const isDigit = /^\d$/.test(key);
                  const isControl = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(key) || e.ctrlKey || e.metaKey;
                  if (!isDigit && !isControl) e.preventDefault();
                  const maxCap = selectedLeaveTypeMaxDays ?? 365;
                  const cur = String(allocationForm.totalDays ?? "").replace(/\D/g, "");
                  if (isDigit && (cur.length >= 3 || Number(cur + key) > maxCap)) e.preventDefault();
                }}
                onChange={(event) => {
                  const raw = event.target.value.replace(/\D/g, "");
                  const maxCap = selectedLeaveTypeMaxDays ?? 365;
                  let val = raw === "" ? "" : String(Math.min(maxCap, Math.max(0, Number(raw))));
                  if (val !== "" && Number(val) < 1) val = "1";
                  setAllocationForm((prev) => ({ ...prev, totalDays: val }));
                }}
                fullWidth
                inputProps={{
                  min: 1,
                  max: selectedLeaveTypeMaxDays ?? 365,
                  inputMode: "numeric",
                }}
                error={Boolean(allocationErrors.totalDays)}
                helperText={
                  allocationErrors.totalDays ||
                  (selectedLeaveTypeMaxDays != null ? `Max for this leave type: ${selectedLeaveTypeMaxDays} day(s)` : null)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={allocationForm.carryForward}
                    onChange={(event) => {
                      const checked = event.target.checked;
                      setAllocationForm((prev) => ({
                        ...prev,
                        carryForward: checked,
                        maxCarryForwardDays: checked ? prev.maxCarryForwardDays : 0,
                      }));
                    }}
                  />
                }
                label="Allow Carry Forward"
              />
            </Grid>
            {allocationForm.carryForward ? (
              <Grid item xs={12}>
                <TextField
                  label="Max Carry Forward Days"
                  required
                  type="number"
                  value={allocationForm.maxCarryForwardDays}
                  onKeyDown={(e) => {
                    const key = e.key;
                    const isDigit = /^\d$/.test(key);
                    const isControl = ["Backspace", "Delete", "Tab", "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown"].includes(key) || e.ctrlKey || e.metaKey;
                    if (!isDigit && !isControl) e.preventDefault();
                    const cur = String(allocationForm.maxCarryForwardDays ?? "").replace(/\D/g, "");
                    const cap = allocationTotalDaysCap ?? 365;
                    if (isDigit && (cur.length >= 3 || Number(cur + key) > cap)) e.preventDefault();
                  }}
                  onChange={(event) => {
                    const raw = event.target.value.replace(/\D/g, "");
                    const cap = allocationTotalDaysCap ?? 365;
                    const val = raw === "" ? "" : String(Math.min(cap, Math.max(0, Number(raw))));
                    setAllocationForm((prev) => ({ ...prev, maxCarryForwardDays: val === "" ? "" : val }));
                  }}
                  fullWidth
                  inputProps={{
                    min: 0,
                    max: allocationTotalDaysCap ?? 365,
                    inputMode: "numeric",
                  }}
                  error={Boolean(allocationErrors.maxCarryForwardDays)}
                  helperText={
                    allocationErrors.maxCarryForwardDays ||
                    (allocationTotalDaysCap != null
                      ? `Cannot exceed total allocation (${allocationTotalDaysCap} day(s))`
                      : "Set total days first")
                  }
                />
              </Grid>
            ) : null}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAllocationDialog} disabled={isSavingAllocation}>
            Cancel
          </Button>
          <PrimaryButton
            onClick={handleAllocationSubmit}
            disabled={!isAllocationFormValid || isSavingAllocation}
            startIcon={
              isSavingAllocation ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {isSavingAllocation
              ? editingAllocation
                ? "Updating..."
                : "Creating..."
              : editingAllocation
                ? "Update"
                : "Create"}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteConfirmOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          closeDeleteConfirmation();
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Leave Type</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete "
            <strong>
              {leaveTypeToDelete?.name || leaveTypeToDelete?.leaveTypeName || "this leave type"}
            </strong>
            "? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteConfirmation}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeletingLeaveType}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveManagement;
